/**
 * Logo d'organisation — source unique : `personalization.logo` (Firestore).
 */

export const ORGANIZATION_LOGO_MAX_BYTES = 2 * 1024 * 1024;

export const ORGANIZATION_LOGO_ACCEPT =
  '.png,.jpg,.jpeg,.svg,.webp,image/png,image/jpeg,image/svg+xml,image/webp';

const ALLOWED_MIME_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/svg+xml',
  'image/webp',
]);

const ALLOWED_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'svg', 'webp']);

export type OrganizationLogoValidationCode = 'not_image' | 'invalid_format' | 'too_large';

export class OrganizationLogoValidationError extends Error {
  readonly code: OrganizationLogoValidationCode;

  constructor(code: OrganizationLogoValidationCode) {
    super(code);
    this.name = 'OrganizationLogoValidationError';
    this.code = code;
  }
}

function fileExtension(file: File): string {
  const fromName = file.name.split('.').pop()?.toLowerCase() ?? '';
  return fromName;
}

/** URL affichée partout (admin, tunnel, dashboard). */
export function getOrganizationLogoUrl(
  configuration: { personalization?: { logo?: string }; logo?: string } | null | undefined,
): string {
  return configuration?.personalization?.logo?.trim() || configuration?.logo?.trim() || '';
}

export function validateOrganizationLogoFile(file: File): void {
  if (file.size > ORGANIZATION_LOGO_MAX_BYTES) {
    throw new OrganizationLogoValidationError('too_large');
  }

  const ext = fileExtension(file);
  const mime = (file.type || '').toLowerCase();

  if (!mime && ext && ALLOWED_EXTENSIONS.has(ext)) {
    return;
  }

  if (!mime.startsWith('image/')) {
    throw new OrganizationLogoValidationError('not_image');
  }

  if (!ALLOWED_MIME_TYPES.has(mime) && !(ext && ALLOWED_EXTENSIONS.has(ext))) {
    throw new OrganizationLogoValidationError('invalid_format');
  }
}

type LogoTranslate = (key: string) => string;

export function getOrganizationLogoErrorMessage(error: unknown, t: LogoTranslate): string {
  if (error instanceof OrganizationLogoValidationError) {
    switch (error.code) {
      case 'too_large':
        return t('admin.configuration.general.fileTooLarge');
      case 'invalid_format':
        return t('admin.configuration.general.logoInvalidFormat');
      case 'not_image':
      default:
        return t('admin.configuration.general.fileMustBeImage');
    }
  }

  if (error instanceof Error && error.message) {
    const msg = error.message.toLowerCase();
    if (msg.includes('2mb') || msg.includes('dépasser')) {
      return t('admin.configuration.general.fileTooLarge');
    }
    if (msg.includes('image')) {
      return t('admin.configuration.general.fileMustBeImage');
    }
  }

  return t('admin.configuration.general.logoUploadFailed');
}
